import { useEffect, useState, useRef } from 'react'
import { useGameStore } from '@/store/gameStore'
import { useAudioContext } from '@/contexts/AudioContext'
import { winCard, winStripe } from '@/assets/ui/panels'
import { btnTryAgain } from '@/assets/ui/buttons'

interface ResultBannerProps {
  type: 'win' | 'lose'
  amount: number
  onClose: () => void
}

// Стили текста для лейблов
const labelStyle: React.CSSProperties = {
  fontFamily: "'Caesar Dressing', cursive",
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '92%',
  letterSpacing: '0%',
  color: '#282E44',
}

// Стили для суммы выигрыша (золотой градиент)
const amountStyle: React.CSSProperties = {
  fontFamily: "'Big Shoulders Display', sans-serif",
  fontWeight: 700,
  fontSize: '36px',
  lineHeight: '92%',
  letterSpacing: '0%',
  background: 'linear-gradient(180deg, #F9D640 0%, #E18635 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
}

export default function ResultBanner({ type, amount, onClose }: ResultBannerProps) {
  const { tower } = useGameStore()
  const { playSfx } = useAudioContext()
  
  // Оба типа появляются мгновенно — одновременно с закрытием ворот
  const [isVisible, setIsVisible] = useState(true)
  const [showCard, setShowCard] = useState(true)
  
  // Ref для onClose чтобы не пересоздавать таймеры при смене ссылки
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose })

  useEffect(() => {
    // Звук победы
    if (type === 'win') {
      playSfx('win')
    }
    
    // Win = 3 сек, Lose (Try Again) = 2 сек
    const displayTime = type === 'win' ? 3000 : 2000
    
    // Автозакрытие — начинаем fade-out
    const t1 = setTimeout(() => {
      setIsVisible(false)
      setShowCard(false)
    }, displayTime)
    
    // Вызываем onClose после анимации fade-out (300ms)
    const t2 = setTimeout(() => {
      onCloseRef.current()
    }, displayTime + 300)
    
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [type, playSfx])

  return (
    <div 
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center transition-all duration-300 pointer-events-none ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div 
        className={`relative z-10 transition-all duration-500 ${
          showCard ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
        }`}
      >
        {type === 'win' ? (
          /* WIN Card — используем изображение как основу */
          <div className="relative flex flex-col items-center" style={{ marginTop: '-50px' }}>
            {/* Фоновое изображение карточки */}
            <img 
              src={winCard}
              alt=""
              className="w-[340px] h-auto"
              draggable={false}
            />
            
            {/* Полоска под карточкой — 314px, высота 15px */}
            <img 
              src={winStripe}
              alt=""
              className="w-[314px]"
              style={{ marginTop: '-14px', height: '15px' }}
              draggable={false}
            />
            
            {/* Оверлей с данными — позиционируем поверх картинки справа, опущен на 20px */}
            <div 
              className="absolute flex flex-col items-center"
              style={{ width: '120px', top: 'calc(50% + 20px)', transform: 'translateY(-50%)', right: '46px' }}
            >
              {/* Rolls */}
              <div className="text-center mb-1">
                <span style={labelStyle}>
                  Rolls: x{tower.length}
                </span>
              </div>

              {/* Winning label */}
              <div className="text-center mb-2">
                <span style={labelStyle}>
                  Winning:
                </span>
              </div>

              {/* Тёмный блок для суммы */}
              <div 
                className="text-center py-2 px-4"
                style={{
                  backgroundColor: '#282E44',
                  borderRadius: '8px',
                }}
              >
                <span style={amountStyle}>
                  {(amount / 100).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          /* LOSE Banner — просто изображение, без кнопки */
          <img 
            src={btnTryAgain}
            alt="TRY AGAIN"
            className="w-[280px]"
            draggable={false}
          />
        )}
      </div>
    </div>
  )
}
